import React, { useState, useEffect, createContext, useContext } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { FiHome, FiGrid, FiTable, FiShoppingCart, FiLogOut, FiMenu, FiX, FiList, FiSettings, FiWifi, FiWifiOff, FiUser, FiUsers, FiPercent, FiPackage, FiChevronDown, FiChevronRight, FiDroplet, FiBox, FiUserCheck, FiBook, FiDollarSign, FiBarChart2, FiTrash2, FiBell, FiAlertTriangle } from 'react-icons/fi';
import { toast } from 'react-toastify';
import io from 'socket.io-client';
import axios from 'axios';

const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || 'https://green-admin.merket.io';
const API_URL = process.env.REACT_APP_API_URL || 'https://green-admin.merket.io/api';

// Create context for socket
export const SocketContext = createContext(null);
export const useSocket = () => useContext(SocketContext);

// Create context for low stock alerts
export const LowStockContext = createContext(null);
export const useLowStock = () => useContext(LowStockContext);

function Layout({ onLogout }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [socket, setSocket] = useState(null);
  const [socketConnected, setSocketConnected] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState(['sales']); // Default expand sales
  
  // Get user role from localStorage
  const userRole = localStorage.getItem('userRole') || 'admin';
  const userName = localStorage.getItem('userName') || 'Admin';
  
  // Low stock alert state
  const [lowStockItems, setLowStockItems] = useState([]);
  const [showLowStockDropdown, setShowLowStockDropdown] = useState(false);

  // Unit labels for display
  const packagingLabels = { box: 'Hộp', can: 'Lon', bottle: 'Chai', bag: 'Túi', piece: 'Quả', item: 'Cái', kg: 'Kg' };
  const measurementLabels = { kg: 'Kg', g: 'Gam', liter: 'Lít', ml: 'ml', piece: 'Cái' };

  const getDisplayUnit = (ingredient) => {
    if (ingredient.fromRecipe) {
      return measurementLabels[ingredient.measurementUnit] || ingredient.measurementUnit;
    }
    return packagingLabels[ingredient.packagingUnit] || ingredient.packagingUnit;
  };

  const allNavigation = [
    { name: 'Dashboard', path: '/dashboard', icon: FiHome },
    { 
      name: 'Bán hàng', 
      icon: FiShoppingCart,
      key: 'sales',
      children: [
        { name: 'Đơn hàng', path: '/orders', icon: FiShoppingCart },
        { name: 'Quản lý bàn', path: '/tables', icon: FiTable },
        { name: 'Khách hàng', path: '/customers', icon: FiUsers },
        { name: 'Khuyến mãi', path: '/discounts', icon: FiPercent },
      ]
    },
    { 
      name: 'Sản phẩm', 
      icon: FiGrid,
      key: 'products',
      children: [
        { name: 'Quản lý món', path: '/menu', icon: FiGrid },
        { name: 'Danh mục', path: '/categories', icon: FiList },
      ]
    },
    { 
      name: 'Quản lý kho', 
      icon: FiPackage,
      key: 'inventory',
      children: [
        { name: 'Vật dụng', path: '/inventory/supplies', icon: FiBox },
        { name: 'Nguyên liệu', path: '/inventory/ingredients', icon: FiDroplet },
        { name: 'Công thức', path: '/inventory/recipes', icon: FiBook },
        { name: 'Hàng hủy', path: '/inventory/wasted', icon: FiTrash2 },
        { name: 'Chi phí', path: '/inventory/expenses', icon: FiDollarSign },
        { name: 'Nhà cung cấp', path: '/inventory/suppliers', icon: FiUserCheck },
      ]
    },
    { name: 'Báo cáo', path: '/reports', icon: FiBarChart2, hideForSales: true },
    { name: 'Profile', path: '/profile', icon: FiUser },
    { 
      name: 'Cài đặt', 
      icon: FiSettings,
      key: 'settings',
      hideForSales: true,
      children: [
        { name: 'Chung', path: '/settings/general', icon: FiSettings },
        { name: 'Cửa hàng', path: '/settings/shop', icon: FiHome },
        { name: 'Tài khoản', path: '/settings/accounts', icon: FiUsers },
        { name: 'Điểm thưởng', path: '/settings/points', icon: FiPercent },
        { name: 'In hóa đơn', path: '/settings/print', icon: FiList },
        { name: 'Đơn hàng', path: '/settings/orders', icon: FiShoppingCart },
      ]
    },
  ];

  // Filter navigation based on role
  const navigation = userRole === 'sales' 
    ? allNavigation.filter(item => !item.hideForSales)
    : allNavigation;

  const isActive = (path) => location.pathname === path;
  const isParentActive = (children) => children?.some(child => location.pathname === child.path || location.pathname.startsWith(child.path + '/'));
  
  const toggleMenu = (key) => {
    setExpandedMenus(prev => 
      prev.includes(key) 
        ? prev.filter(k => k !== key)
        : [...prev, key]
    );
  };

  // Auto expand parent menu when child is active
  useEffect(() => {
    navigation.forEach(item => {
      if (item.children && isParentActive(item.children)) {
        if (!expandedMenus.includes(item.key)) {
          setExpandedMenus(prev => [...prev, item.key]);
        }
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Load low stock ingredients
  const loadLowStockItems = async () => {
    try {
      const response = await axios.get(`${API_URL}/ingredients/alerts/low-stock`);
      if (response.data.success) {
        setLowStockItems(response.data.data || []);
      }
    } catch (error) {
      console.error('Error loading low stock items:', error);
    }
  };

  // Load low stock on mount and periodically
  useEffect(() => {
    loadLowStockItems();
    // Refresh every 5 minutes
    const interval = setInterval(loadLowStockItems, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showLowStockDropdown && !event.target.closest('.low-stock-dropdown-container')) {
        setShowLowStockDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showLowStockDropdown]);

  // Setup Socket.IO connection - runs once when Layout mounts
  useEffect(() => {
    console.log('🔌 Connecting to socket:', SOCKET_URL);
    const newSocket = io(SOCKET_URL, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 20000
    });

    newSocket.on('connect', () => {
      console.log('✅ Socket connected:', newSocket.id);
      setSocketConnected(true);
      newSocket.emit('join-admin');
      console.log('📢 Joined admin-room');
      // Removed: toast.success('Kết nối thông báo thành công')
    });

    newSocket.on('disconnect', (reason) => {
      console.log('❌ Socket disconnected:', reason);
      setSocketConnected(false);
      if (reason !== 'io client disconnect') {
        toast.warning('Mất kết nối thông báo, đang kết nối lại...', { autoClose: 3000, toastId: 'socket-disconnected' });
      }
    });

    newSocket.on('reconnect', (attemptNumber) => {
      console.log('🔄 Socket reconnected after', attemptNumber, 'attempts');
      // Removed: toast.success('Đã kết nối lại thành công!')
    });

    newSocket.on('connect_error', (error) => {
      console.error('Socket connection error:', error);
      setSocketConnected(false);
    });

    // Handle new order notification
    newSocket.on('new-order', (data) => {
      console.log('🔔 New order received:', data);
      const order = data.order;
      const tableName = order.table?.name || `Bàn ${order.tableNumber}`;
      const itemCount = order.items.reduce((sum, item) => sum + item.quantity, 0);
      const itemText = `${itemCount} món`;
      
      // Toast notification with click handler
      toast.info(
        <div onClick={() => navigateToOrder(order._id)} className="cursor-pointer">
          <div className="font-bold">🔔 {data.isUpdate ? 'Cập nhật đơn' : 'Đơn mới'} từ {tableName}!</div>
          <div className="text-sm mt-1">{itemText} - Click để xem chi tiết</div>
        </div>,
        {
          autoClose: 10000,
          onClick: () => navigateToOrder(order._id),
          toastId: `order-${order._id}`
        }
      );
      
      // Browser notification
      showBrowserNotification(tableName, `${data.isUpdate ? 'Cập nhật' : 'Đã đặt'} ${itemText}`, order._id);
      
      // Play sound with speech - pass order details
      playNotificationSound(tableName, itemCount, data.isUpdate);
    });

    newSocket.on('order-updated', (data) => {
      console.log('📝 Order updated:', data);
      // Removed toast to reduce notification noise
    });

    newSocket.on('payment-completed', (data) => {
      console.log('💰 Payment completed:', data);
      // Removed toast to reduce notification noise
    });

    // Handle payment received from webhook (QR payment)
    newSocket.on('payment-received', (data) => {
      console.log('💳 Payment received:', data);
      const order = data.order;
      if (order) {
        const tableName = order.table?.name || (order.tableNumber ? `Bàn ${order.tableNumber}` : '');
        const amount = order.totalAmount || 0;
        // Play TTS notification
        playPaymentNotificationSound(tableName, amount);
      }
    });

    // Handle delivery reminder (30 min / 10 min before scheduled delivery time)
    newSocket.on('delivery-reminder', (data) => {
      console.log('⏰ Delivery reminder:', data);
      const target = data.customerName || data.tableLabel || '';
      const minutes = data.minutes || 30;
      // Toast
      toast.warning(
        <div onClick={() => navigateToOrder(data.orderId)} className="cursor-pointer">
          <div className="font-bold">⏰ Còn {minutes} phút tới giờ giao đơn</div>
          <div className="text-sm mt-1">{target ? `Khách: ${target}` : 'Đơn hàng sắp tới giờ giao'} - Click để xem</div>
        </div>,
        {
          autoClose: 15000,
          toastId: `delivery-${data.orderId}-${data.phase}`
        }
      );
      // Browser notification
      showBrowserNotification(`Còn ${minutes} phút tới giờ giao đơn`, target ? `Khách: ${target}` : '', data.orderId);
      // Play TTS
      playDeliveryReminderSound(minutes, target);
    });

    newSocket.on('orders-cleared', (data) => {
      console.log('🗑️ Orders cleared:', data);
      // Removed toast to reduce notification noise
    });

    // Listen for app update notifications
    newSocket.on('app-update-available', (data) => {
      console.log('🚀 App update available:', data);
      // Dispatch custom event for Settings component to listen
      window.dispatchEvent(new CustomEvent('app-update-available', { detail: data }));
      // Show toast notification only in Electron app
      if (window.isElectronApp) {
        toast.info(`🚀 Có phiên bản mới ${data.version}! Vào Cài đặt > Thông tin để cập nhật.`, {
          autoClose: 10000
        });
      }
    });

    // Handle bank account switch - force logout to ensure fresh data
    newSocket.on('bank-account-switched', (data) => {
      console.log('[Bank Switch] Received bank account switch event:', data);
      
      // Show notification about bank switch
      toast.info(
        <div>
          <div className="font-bold">🏦 Đã chuyển tài khoản mặc định</div>
          <div className="text-sm mt-1">{data.bankName} - {data.accountNumber}</div>
          <div className="text-xs mt-1 text-gray-500">Hệ thống sẽ tự động đăng xuất sau 3 giây...</div>
        </div>,
        { autoClose: 3000, toastId: 'bank-switch' }
      );
      
      // Auto logout after 3 seconds to ensure fresh data
      setTimeout(() => {
        console.log('[Bank Switch] Auto logout due to bank account change');
        if (onLogout) {
          onLogout();
        } else {
          // Fallback: clear local storage and reload
          localStorage.clear();
          window.location.href = '/';
        }
      }, 3000);
    });

    setSocket(newSocket);

    // Request notification permission
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }

    // Preload speech synthesis voices
    if ('speechSynthesis' in window) {
      window.speechSynthesis.getVoices();
      window.speechSynthesis.onvoiceschanged = () => {
        window.speechSynthesis.getVoices();
      };
    }

    // Cleanup only when Layout unmounts (i.e., logout)
    return () => {
      console.log('🔌 Disconnecting socket (Layout unmounting)');
      newSocket.close();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const navigateToOrder = (orderId) => {
    navigate('/orders', { state: { highlightOrderId: orderId, switchToTab: 'pending' } });
  };

  const showBrowserNotification = (tableName, message, orderId) => {
    if ('Notification' in window && Notification.permission === 'granted') {
      const notification = new Notification(`Green Cafe - ${tableName}`, {
        body: message,
        icon: '/logo192.png',
        badge: '/logo192.png',
        tag: `order-${orderId}`,
        requireInteraction: true,
        data: { orderId, url: '/orders' }
      });

      notification.onclick = () => {
        window.focus();
        navigateToOrder(orderId);
        notification.close();
      };
    }
  };

  // TTS configuration - use backend API (Google Translate TTS)
  const API_URL = process.env.REACT_APP_API_URL || 'https://green-admin.merket.io/api';
  
  // Get TTS settings from localStorage
  const getTtsSettings = () => {
    try {
      const saved = localStorage.getItem('ttsSettings');
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (e) {
      console.log('Error reading TTS settings:', e);
    }
    return { enabled: true, beepEnabled: true, speed: 'normal' };
  };
  
  const playNotificationSound = async (tableName = '', itemCount = 0, isUpdate = false) => {
    try {
      const ttsSettings = getTtsSettings();
      
      // Play beep sound if enabled
      if (ttsSettings.beepEnabled) {
        const beepAudio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBSuBzvLZiTYIG2m98OScTgwOUKfj8LZjHAU7k9jzy3ksBSN1xe/glEILElyz6OyrWBUIRp/g8r5sIAUqf83y2ok4CBlpu/DlnFAMDlGo5PC3ZBwGOpPY88t5LAUjdcXv4JRCCxFctOntq1kVB0af4PK+bCAFKoDN8tmJOAgZaLvw5Z1QDA5RqOTwt2QcBjqT2PPLeSYIJnXG7+CUQgsRXLTp7atZFQdGn+DyvmwgBSp/zfLaiTgIGWm78OWdUAwOUajk8LdkHAY6k9jzy3kmCCZ1xu/glEILEVy06e2rWRUHRp/g8r5sIAUqf83y2ok4CBlpu/DlnVAMDlGo5PC3ZBwGOpPY88t5JggmdcXv4JRCCxFctOntq1kVB0af4PK+bCAFKn/N8tqJOAgZabvw5Z1QDA5RqOTwt2QcBjqT2PPLeSYIJnXF7+CUQgsRXLTp7atZFQdGn+DyvmwgBSp/zfLaiTgIGWm78OWdUAwOUajk8LdkHAY6k9jzy3kmCCZ1xe/glEILEVy06e2rWRUHRp/g8r5sIAUqf83y2ok4CBlpu/DlnVAMDlGo5PC3ZBwGOpPY88t5JggmdcXv4JRCCxFctOntq1kVB0af4PK+bCAFKn/N8tqJOAgZabvw5Z1QDA5RqOTwt2QcBjqT2PPLeSYIJnXF7+CUQgsRXLTp7atZFQdGn+DyvmwgBSp/zfLaiTgIGWm78OWdUAwOUajk8LdkHAY6k9jzy3kmCCZ1xe/glEILEVy06e2rWRUHRp/g8r5sIAUqf83y2ok4CBlpu/DlnVAMDlGo5PC3ZBwGOpPY88t5JggmdcXv4JRCCxFctOntq1kVB0af4PK+bCAFKn/N8tqJOAgZabvw5Z1QDA5RqOTwt2QcBjqT2PPLeSYIJnXF7+CUQgsRXLTp7atZFQdGn+DyvmwgBSp/zfLaiTgIGWm78OWdUAwOUajk8LdkHAY6k9jzy3kmCCZ1xe/glEILEVy06e2rWRUHRp/g8r5sIAUqf83y2ok4CBlpu/DlnVAMDlGo5PC3ZBwGOpPY88t5JggmdcXv4JRCCxFctOntq1kVB0af4PK+bCAFKn/N8tqJOAgZabvw5Z1QDA5RqOTwt2QcBjqT2PPLeSYIJnXF7+CUQgsRXLTp7atZFQdGn+DyvmwgBSp/zfLaiTgIGWm78OWdUAwOUajk8LdkHAY6k9jzy3kmCCZ1xe/glEILEVy06e2rWRUHRp/g8r5sIAUqf83y2ok4CBlpu/DlnVAMDlGo5PC3ZBwGOpPY88t5JggmdcXv4JRCCxFctOntq1kVB0af4PK+bCAFKn/N8tqJOAgZabvw5Z1QDA5RqOTwt2QcBjqT2PPLeSYIJnXF7+CUQg==');
        beepAudio.play().catch(e => console.log('Could not play beep:', e));
      }
      
      // Skip TTS if disabled
      if (!ttsSettings.enabled) {
        return;
      }
      
      // Build speech message
      let speechText = 'Có gọi món';
      if (tableName && itemCount > 0) {
        if (isUpdate) {
          speechText = `${tableName} thêm ${itemCount} món`;
        } else {
          speechText = `${tableName} gọi ${itemCount} món`;
        }
      }
      
      // Wait for beep to finish (if enabled)
      const delay = ttsSettings.beepEnabled ? 300 : 0;
      setTimeout(async () => {
        // Try backend TTS API (Google Translate TTS)
        try {
          const speedParam = ttsSettings.speed === 'slow' ? '&slow=1' : '';
          const audioUrl = `${API_URL}/tts/speak?text=${encodeURIComponent(speechText)}${speedParam}`;
          const ttsAudio = new Audio(audioUrl);
          ttsAudio.play().catch(e => {
            console.log('Google TTS failed, falling back to browser TTS:', e);
            fallbackBrowserTTS(speechText, ttsSettings.speed);
          });
        } catch (error) {
          console.log('TTS error, falling back to browser TTS:', error);
          fallbackBrowserTTS(speechText, ttsSettings.speed);
        }
      }, delay);
    } catch (error) {
      console.error('Error playing notification:', error);
    }
  };
  
  const fallbackBrowserTTS = (text, speed = 'normal') => {
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = 'vi-VN';
      utterance.rate = speed === 'slow' ? 0.7 : speed === 'fast' ? 1.3 : 1.0;
      utterance.pitch = 1.0;
      utterance.volume = 1.0;
      
      const voices = window.speechSynthesis.getVoices();
      const vietnameseVoice = voices.find(voice => 
        voice.lang.includes('vi') || voice.name.includes('Vietnam')
      );
      if (vietnameseVoice) {
        utterance.voice = vietnameseVoice;
      }
      
      window.speechSynthesis.speak(utterance);
    }
  };

  // Convert number to Vietnamese words
  const numberToVietnameseWords = (num) => {
    if (num === 0) return 'không';
    
    const ones = ['', 'một', 'hai', 'ba', 'bốn', 'năm', 'sáu', 'bảy', 'tám', 'chín'];
    const teens = ['mười', 'mười một', 'mười hai', 'mười ba', 'mười bốn', 'mười lăm', 'mười sáu', 'mười bảy', 'mười tám', 'mười chín'];
    
    const convertHundreds = (n) => {
      if (n === 0) return '';
      if (n < 10) return ones[n];
      if (n < 20) return teens[n - 10];
      if (n < 100) {
        const ten = Math.floor(n / 10);
        const one = n % 10;
        if (one === 0) return ones[ten] + ' mươi';
        if (one === 1) return ones[ten] + ' mươi mốt';
        if (one === 5) return ones[ten] + ' mươi lăm';
        return ones[ten] + ' mươi ' + ones[one];
      }
      const hundred = Math.floor(n / 100);
      const remainder = n % 100;
      if (remainder === 0) return ones[hundred] + ' trăm';
      if (remainder < 10) return ones[hundred] + ' trăm lẻ ' + ones[remainder];
      return ones[hundred] + ' trăm ' + convertHundreds(remainder);
    };
    
    // Handle thousands (for amounts like 40,000 -> 40 nghìn)
    const thousands = Math.floor(num / 1000);
    if (thousands > 0 && num % 1000 === 0) {
      return convertHundreds(thousands) + ' nghìn';
    }
    
    return convertHundreds(num);
  };

  // Play payment notification sound with TTS
  const playPaymentNotificationSound = async (tableName = '', amount = 0) => {
    try {
      const ttsSettings = getTtsSettings();
      
      // Play beep sound if enabled
      if (ttsSettings.beepEnabled) {
        const beepAudio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBSuBzvLZiTYIG2m98OScTgwOUKfj8LZjHAU7k9jzy3ksBSN1xe/glEILElyz6OyrWBUIRp/g8r5sIAUqf83y2ok4CBlpu/DlnFAMDlGo5PC3ZBwGOpPY88t5LAUjdcXv4JRCCxFctOntq1kVB0af4PK+bCAFKoDN8tmJOAgZaLvw5Z1QDA5RqOTwt2QcBjqT2PPLeSYIJnXG7+CUQgsRXLTp7atZFQdGn+DyvmwgBSp/zfLaiTgIGWm78OWdUAwOUajk8LdkHAY6k9jzy3kmCCZ1xu/glEILEVy06e2rWRUHRp/g8r5sIAUqf83y2ok4CBlpu/DlnVAMDlGo5PC3ZBwGOpPY88t5JggmdcXv4JRCCxFctOntq1kVB0af4PK+bCAFKn/N8tqJOAgZabvw5Z1QDA5RqOTwt2QcBjqT2PPLeSYIJnXF7+CUQgsRXLTp7atZFQdGn+DyvmwgBSp/zfLaiTgIGWm78OWdUAwOUajk8LdkHAY6k9jzy3kmCCZ1xe/glEILEVy06e2rWRUHRp/g8r5sIAUqf83y2ok4CBlpu/DlnVAMDlGo5PC3ZBwGOpPY88t5JggmdcXv4JRCCxFctOntq1kVB0af4PK+bCAFKn/N8tqJOAgZabvw5Z1QDA5RqOTwt2QcBjqT2PPLeSYIJnXF7+CUQgsRXLTp7atZFQdGn+DyvmwgBSp/zfLaiTgIGWm78OWdUAwOUajk8LdkHAY6k9jzy3kmCCZ1xe/glEILEVy06e2rWRUHRp/g8r5sIAUqf83y2ok4CBlpu/DlnVAMDlGo5PC3ZBwGOpPY88t5JggmdcXv4JRCCxFctOntq1kVB0af4PK+bCAFKn/N8tqJOAgZabvw5Z1QDA5RqOTwt2QcBjqT2PPLeSYIJnXF7+CUQgsRXLTp7atZFQdGn+DyvmwgBSp/zfLaiTgIGWm78OWdUAwOUajk8LdkHAY6k9jzy3kmCCZ1xe/glEILEVy06e2rWRUHRp/g8r5sIAUqf83y2ok4CBlpu/DlnVAMDlGo5PC3ZBwGOpPY88t5JggmdcXv4JRCCxFctOntq1kVB0af4PK+bCAFKn/N8tqJOAgZabvw5Z1QDA5RqOTwt2QcBjqT2PPLeSYIJnXF7+CUQgsRXLTp7atZFQdGn+DyvmwgBSp/zfLaiTgIGWm78OWdUAwOUajk8LdkHAY6k9jzy3kmCCZ1xe/glEILEVy06e2rWRUHRp/g8r5sIAUqf83y2ok4CBlpu/DlnVAMDlGo5PC3ZBwGOpPY88t5JggmdcXv4JRCCxFctOntq1kVB0af4PK+bCAFKn/N8tqJOAgZabvw5Z1QDA5RqOTwt2QcBjqT2PPLeSYIJnXF7+CUQg==');
        beepAudio.play().catch(e => console.log('Could not play beep:', e));
      }
      
      // Skip TTS if disabled
      if (!ttsSettings.enabled) {
        return;
      }
      
      // Convert amount to thousands for speech (e.g., 40000 -> 40)
      const amountInThousands = Math.round(amount / 1000);
      const amountText = numberToVietnameseWords(amountInThousands) + ' nghìn đồng';
      
      // Build speech message
      let speechText;
      if (tableName) {
        speechText = `${tableName} đã thanh toán ${amountText}`;
      } else {
        speechText = `Đã nhận được ${amountText}`;
      }
      
      // Wait for beep to finish (if enabled)
      const delay = ttsSettings.beepEnabled ? 300 : 0;
      setTimeout(async () => {
        try {
          const speedParam = ttsSettings.speed === 'slow' ? '&slow=1' : '';
          const audioUrl = `${API_URL}/tts/speak?text=${encodeURIComponent(speechText)}${speedParam}`;
          const ttsAudio = new Audio(audioUrl);
          ttsAudio.play().catch(e => {
            console.log('Google TTS failed, falling back to browser TTS:', e);
            fallbackBrowserTTS(speechText, ttsSettings.speed);
          });
        } catch (error) {
          console.log('TTS error, falling back to browser TTS:', error);
          fallbackBrowserTTS(speechText, ttsSettings.speed);
        }
      }, delay);
    } catch (error) {
      console.error('Error playing payment notification:', error);
    }
  };

  // Play delivery reminder sound with TTS
  const playDeliveryReminderSound = async (minutes = 30, customerName = '') => {
    try {
      const ttsSettings = getTtsSettings();

      // Play beep sound if enabled
      if (ttsSettings.beepEnabled) {
        const beepAudio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBSuBzvLZiTYIG2m98OScTgwOUKfj8LZjHAU7k9jzy3ksBSN1xe/glEILElyz6OyrWBUIRp/g8r5sIAUqf83y2ok4CBlpu/DlnFAMDlGo5PC3ZBwGOpPY88t5LAUjdcXv4JRCCxFctOntq1kVB0af4PK+bCAFKoDN8tmJOAgZaLvw5Z1QDA5RqOTwt2QcBjqT2PPLeSYIJnXG7+CUQgsRXLTp7atZFQdGn+DyvmwgBSp/zfLaiTgIGWm78OWdUAwOUajk8LdkHAY6k9jzy3kmCCZ1xu/glEILEVy06e2rWRUHRp/g8r5sIAUqf83y2ok4CBlpu/DlnVAMDlGo5PC3ZBwGOpPY88t5JggmdcXv4JRCCxFctOntq1kVB0af4PK+bCAFKn/N8tqJOAgZabvw5Z1QDA5RqOTwt2QcBjqT2PPLeSYIJnXF7+CUQg==');
        beepAudio.play().catch(e => console.log('Could not play beep:', e));
      }

      // Skip TTS if disabled
      if (!ttsSettings.enabled) {
        return;
      }

      // Build speech message
      let speechText;
      if (customerName) {
        speechText = `Còn ${minutes} phút tới giờ giao đơn cho khách ${customerName}`;
      } else {
        speechText = `Còn ${minutes} phút tới giờ giao đơn`;
      }

      // Wait for beep to finish (if enabled)
      const delay = ttsSettings.beepEnabled ? 300 : 0;
      setTimeout(async () => {
        try {
          const speedParam = ttsSettings.speed === 'slow' ? '&slow=1' : '';
          const audioUrl = `${API_URL}/tts/speak?text=${encodeURIComponent(speechText)}${speedParam}`;
          const ttsAudio = new Audio(audioUrl);
          ttsAudio.play().catch(e => {
            console.log('Google TTS failed, falling back to browser TTS:', e);
            fallbackBrowserTTS(speechText, ttsSettings.speed);
          });
        } catch (error) {
          console.log('TTS error, falling back to browser TTS:', error);
          fallbackBrowserTTS(speechText, ttsSettings.speed);
        }
      }, delay);
    } catch (error) {
      console.error('Error playing delivery reminder:', error);
    }
  };

  return (
    <SocketContext.Provider value={{ socket, socketConnected }}>
      <LowStockContext.Provider value={{ lowStockItems, refreshLowStock: loadLowStockItems }}>
      <div className="bg-gray-100 lg:min-h-screen">
        {/* Desktop Low Stock Alert Banner */}
        {lowStockItems.length > 0 && (
          <div className="hidden lg:block fixed top-0 left-64 right-0 bg-orange-500 text-white z-40">
            <div className="px-4 py-2 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FiAlertTriangle size={18} />
                <span className="font-medium">
                  Cảnh báo: {lowStockItems.length} nguyên liệu sắp hết
                </span>
                <span className="text-orange-100 text-sm">
                  ({lowStockItems.slice(0, 3).map(i => i.name).join(', ')}{lowStockItems.length > 3 ? '...' : ''})
                </span>
              </div>
              <Link 
                to="/inventory/ingredients" 
                state={{ filter: 'lowStock' }}
                className="px-3 py-1 bg-orange-600 hover:bg-orange-700 rounded text-sm"
              >
                Xem chi tiết
              </Link>
            </div>
          </div>
        )}

        {/* Mobile menu button - Hide on mobile when showing bottom nav */}
        <div className="lg:hidden fixed top-0 left-0 right-0 bg-white shadow-md z-50 px-4 py-2 flex justify-between items-center">
          <h1 className="text-lg font-bold text-green-600">Green Cafe</h1>
          <div className="flex items-center space-x-2">
            {/* Socket status indicator */}
            <div className={`flex items-center ${socketConnected ? 'text-green-600' : 'text-red-600'}`}>
              {socketConnected ? <FiWifi size={16} /> : <FiWifiOff size={16} />}
            </div>
            
            {/* Low Stock Notification Icon */}
            <div className="relative low-stock-dropdown-container" style={{ marginTop: '3px' }}>
              <button
                onClick={() => setShowLowStockDropdown(!showLowStockDropdown)}
                className={`p-1 rounded-md relative ${lowStockItems.length > 0 ? 'text-orange-500' : 'text-green-600'}`}
              >
                <FiBell size={16} />
                {lowStockItems.length > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-red-500 text-white text-[10px] rounded-full flex items-center justify-center font-medium">
                    {lowStockItems.length > 9 ? '9+' : lowStockItems.length}
                  </span>
                )}
              </button>
              
              {/* Dropdown */}
              {showLowStockDropdown && (
                <div className="absolute right-0 top-full mt-2 w-72 bg-white rounded-lg shadow-xl border z-[100] max-h-80 overflow-hidden">
                  <div className="px-3 py-2 border-b bg-orange-50 flex items-center gap-2">
                    <FiAlertTriangle className="text-orange-500" size={16} />
                    <span className="font-medium text-orange-700">Nguyên liệu sắp hết</span>
                  </div>
                  {lowStockItems.length === 0 ? (
                    <div className="px-3 py-4 text-center text-gray-500 text-sm">
                      Không có nguyên liệu nào sắp hết
                    </div>
                  ) : (
                    <div className="max-h-60 overflow-y-auto divide-y">
                      {lowStockItems.map(item => (
                        <div key={item._id} className="px-3 py-2 hover:bg-gray-50">
                          <div className="font-medium text-sm">{item.name}</div>
                          <div className="flex justify-between text-xs text-gray-500 mt-0.5">
                            <span>Còn: <span className="text-red-600 font-medium">{item.quantity} {getDisplayUnit(item)}</span></span>
                            <span>Mức cảnh báo: {item.minStock} {getDisplayUnit(item)}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  <Link 
                    to="/inventory/ingredients"
                    state={{ filter: 'lowStock' }}
                    onClick={() => setShowLowStockDropdown(false)}
                    className="block px-3 py-2 text-center text-sm text-orange-600 hover:bg-orange-50 border-t font-medium"
                  >
                    Xem nguyên liệu sắp hết
                  </Link>
                </div>
              )}
            </div>
            
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="p-1.5 rounded-md text-gray-600 hover:bg-gray-100"
            >
              {sidebarOpen ? <FiX size={22} /> : <FiMenu size={22} />}
            </button>
          </div>
        </div>

        {/* Sidebar */}
        <div className={`fixed inset-y-0 left-0 transform ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0 transition-transform duration-300 ease-in-out w-64 bg-white shadow-lg z-[60]`}>
          <div className="h-full flex flex-col">
            {/* Logo */}
            <div className="px-6 py-4 lg:py-6 border-b">
              <h1 className="text-xl lg:text-2xl font-bold text-green-600">Green Cafe</h1>
              <p className="text-xs lg:text-sm text-gray-500 mt-1">Admin Panel</p>
              {/* Socket status */}
              <div className={`flex items-center space-x-2 mt-1.5 lg:mt-2 text-xs lg:text-sm ${socketConnected ? 'text-green-600' : 'text-red-600'}`}>
                {socketConnected ? <FiWifi size={14} /> : <FiWifiOff size={14} />}
                <span>{socketConnected ? 'Đang kết nối' : 'Mất kết nối'}</span>
              </div>
            </div>

            {/* Navigation */}
            <nav className="flex-1 px-3 lg:px-4 py-3 lg:py-6 space-y-1 overflow-y-auto">
              {navigation.map((item) => {
                const Icon = item.icon;
                
                // Parent menu with children
                if (item.children) {
                  const isExpanded = expandedMenus.includes(item.key);
                  const hasActiveChild = isParentActive(item.children);
                  
                  return (
                    <div key={item.key}>
                      <button
                        onClick={() => toggleMenu(item.key)}
                        className={`flex items-center justify-between w-full px-3 lg:px-4 py-2 lg:py-2.5 rounded-lg transition-colors text-sm lg:text-base ${
                          hasActiveChild
                            ? 'bg-green-50 text-green-600 font-semibold'
                            : 'text-gray-700 hover:bg-gray-100'
                        }`}
                      >
                        <div className="flex items-center space-x-3">
                          <Icon size={18} className="lg:w-5 lg:h-5 flex-shrink-0" />
                          <span>{item.name}</span>
                        </div>
                        {isExpanded ? <FiChevronDown size={16} /> : <FiChevronRight size={16} />}
                      </button>
                      
                      {/* Children */}
                      {isExpanded && (
                        <div className="ml-4 mt-1 space-y-1 border-l-2 border-gray-100 pl-3">
                          {item.children.map((child) => {
                            const ChildIcon = child.icon;
                            return (
                              <Link
                                key={child.path}
                                to={child.path}
                                onClick={() => setSidebarOpen(false)}
                                className={`flex items-center space-x-3 px-3 py-2 rounded-lg transition-colors text-sm ${
                                  isActive(child.path)
                                    ? 'bg-green-100 text-green-700 font-medium'
                                    : 'text-gray-600 hover:bg-gray-50'
                                }`}
                              >
                                <ChildIcon size={16} className="flex-shrink-0" />
                                <span>{child.name}</span>
                              </Link>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                }
                
                // Regular menu item
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setSidebarOpen(false)}
                    className={`flex items-center space-x-3 px-3 lg:px-4 py-2 lg:py-2.5 rounded-lg transition-colors text-sm lg:text-base ${
                      isActive(item.path)
                        ? 'bg-green-50 text-green-600 font-semibold'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <Icon size={18} className="lg:w-5 lg:h-5 flex-shrink-0" />
                    <span>{item.name}</span>
                  </Link>
                );
              })}
              
              {/* Logout - nằm trong nav, ngay sau các menu items */}
              <div className="pt-2 mt-2 border-t border-gray-100">
                <button
                  onClick={onLogout}
                  className="flex items-center space-x-3 px-3 lg:px-4 py-2 lg:py-3 w-full rounded-lg text-red-600 hover:bg-red-50 transition-colors text-sm lg:text-base"
                >
                  <FiLogOut size={18} className="lg:w-5 lg:h-5 flex-shrink-0" />
                  <span>Đăng xuất</span>
                </button>
              </div>
            </nav>
          </div>
        </div>

        {/* Main content */}
        <div className={`lg:ml-64 pt-12 pb-20 lg:pb-0 ${lowStockItems.length > 0 ? 'lg:pt-10' : 'lg:pt-0'}`}>
          <main className="p-4 lg:p-6">
            <Outlet />
          </main>
        </div>

        {/* Bottom Navigation for Mobile */}
        <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-50">
          <div className="flex justify-around items-center py-1">
            <Link
              to="/dashboard"
              className={`flex flex-col items-center py-1.5 px-3 ${isActive('/dashboard') ? 'text-green-600' : 'text-gray-500'}`}
            >
              <FiHome size={20} />
              <span className="text-xs mt-0.5">Quản lý</span>
            </Link>
            <Link
              to="/orders"
              className={`flex flex-col items-center py-1.5 px-3 ${isActive('/orders') ? 'text-green-600' : 'text-gray-500'}`}
            >
              <FiShoppingCart size={20} />
              <span className="text-xs mt-0.5">Đơn hàng</span>
            </Link>
            <Link
              to="/menu"
              className={`flex flex-col items-center py-1.5 px-3 ${isActive('/menu') ? 'text-green-600' : 'text-gray-500'}`}
            >
              <FiGrid size={20} />
              <span className="text-xs mt-0.5">Sản phẩm</span>
            </Link>
            <Link
              to="/tables"
              className={`flex flex-col items-center py-1.5 px-3 ${isActive('/tables') ? 'text-green-600' : 'text-gray-500'}`}
            >
              <FiTable size={20} />
              <span className="text-xs mt-0.5">Bàn</span>
            </Link>
            <button
              onClick={() => setSidebarOpen(true)}
              className="flex flex-col items-center py-1.5 px-3 text-gray-500"
            >
              <FiMenu size={20} />
              <span className="text-xs mt-0.5">Thêm</span>
            </button>
          </div>
        </div>

        {/* Overlay for mobile */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-[55] lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}
      </div>
      </LowStockContext.Provider>
    </SocketContext.Provider>
  );
}

export default Layout;
